import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { PillToggleButtonComponent , PillToggleButtonColorTheme } from "@components/pill-toggle-button/pill-toggle-button.component";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { SkogsvardAtgardsGrupp } from "~app/common/models/constants";
import { Atgard } from "../../models/atgard-models";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { VkKartaMediatorService } from "../../services/vk-karta-mediator.service";

type SkogsvardMapLayerToggleItem = {
    colorTheme: PillToggleButtonColorTheme;
    group: string;
    text: string;
};

@Component({
    selector: "rask-skogsvard-map-layer-toggle",
    templateUrl: "./skogsvard-map-layer-toggle.component.html",
    styleUrls: ["./skogsvard-map-layer-toggle.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PillToggleButtonComponent,
        FormsModule
    ]
})

export class SkogsvardMapLayerToggleComponent implements OnInit, OnDestroy {
    protected atgardTypes: SkogsvardMapLayerToggleItem[] = [];
    protected selectedTypeGroup: string;

    private readonly allowedAtgardTypes: SkogsvardMapLayerToggleItem[] = [
        {
            group: SkogsvardAtgardsGrupp.rojning,
            text: "RÖJ",
            colorTheme: "skogsvardrojning"
        },
        {
            group: SkogsvardAtgardsGrupp.markberedning,
            text: "MB",
            colorTheme: "skogsvardmarkberedning"
        },
        {
            group: SkogsvardAtgardsGrupp.foryngring,
            text: "PL",
            colorTheme: "skogsvardforyngring"
        },
        {
            group: SkogsvardAtgardsGrupp.inventering,
            text: "INVT",
            colorTheme: "skogsvardinventering"
        },
        {
            group: SkogsvardAtgardsGrupp.ovrigt,
            text: "ÖVRIGT",
            colorTheme: "skogsvardovrigt"
        }
    ];
    private readonly kontraktMediator = inject(KontraktMediatorService);
    private readonly unSubscribe$ = new Subject<void>();
    private readonly vkKartaService = inject(VkKartaMediatorService);

    private atgarder: Atgard[] = [];

    ngOnDestroy(): void {
        this.unSubscribe$.next();
        this.unSubscribe$.complete();
    }

    ngOnInit(): void {
        this.kontraktMediator.atgarder$
            .pipe(takeUntil(this.unSubscribe$))
            .subscribe(atgarder => {
                this.setupTypes(atgarder);
            });

        this.kontraktMediator.atgarderLoaded$
            .pipe(takeUntil(this.unSubscribe$))
            .subscribe(loaded => {
                if (loaded) {
                    setTimeout(() => {
                        this.preSelectVirtualSkogsvardLayer();
                    }, 300);
                }
            });
    }

    protected hoverChange(item: SkogsvardMapLayerToggleItem, hovering: boolean) {
        this.vkKartaService.highlightVirtualSkogsvardLayer({ group: item.group,
            active: hovering });
    }

    protected toggled(item: SkogsvardMapLayerToggleItem, toggleState: boolean) {
        this.toggleLayer(item, toggleState);
        if (toggleState) {
            this.hoverChange(item, true);
        }
    }

    private preSelectVirtualSkogsvardLayer() {
        if (!this.atgarder) {
            return;
        }
        for (const atgard of this.atgarder) {
            const itemToggel = this.atgardTypes.find(x => x.group === atgard.atgardsgrupp);
            if (itemToggel) {
                this.toggleLayer(itemToggel, true);
                break;
            }
        }
    }

    private setupTypes(atgarder: Atgard[]) {
        this.atgarder = atgarder;
        this.atgardTypes = [];
        this.allowedAtgardTypes.forEach(typeDemand => {
            if (atgarder.find(x => x.atgardsgrupp === typeDemand.group)) {
                this.atgardTypes.push(typeDemand);
            }
        });
    }

    private toggleLayer(item: SkogsvardMapLayerToggleItem, toggleState: boolean) {
        this.selectedTypeGroup = item.group;
        this.vkKartaService.activateVirtualSkogsvardLayer({ group: item.group,
            active: toggleState });
    }
}
